import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobCard = ({ job, onWishlistToggle }) => {
  const navigate = useNavigate();
  const [isWishlisted, setIsWishlisted] = useState(job?.isWishlisted || false);

  const handleWishlistClick = (e) => {
    e?.stopPropagation();
    setIsWishlisted(!isWishlisted);
    onWishlistToggle(job?.id, !isWishlisted);
  };

  const handleCardClick = () => {
    navigate(`/job-details?id=${job?.id}`);
  };

  const handleApplyClick = (e) => {
    e?.stopPropagation();
    navigate(`/job-application?jobId=${job?.id}`);
  };

  const formatSalary = (min, max) => {
    const formatNumber = (num) => {
      if (num >= 10000000) return `${(num / 10000000)?.toFixed(1)}Cr`;
      if (num >= 100000) return `${(num / 100000)?.toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000)?.toFixed(1)}K`;
      return num?.toString();
    };
    return `₹${formatNumber(min)} - ₹${formatNumber(max)}`;
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));
    
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  return (
    <div 
      onClick={handleCardClick}
      className="bg-card border border-border rounded-lg p-4 sm:p-6 hover:shadow-modal transition-smooth cursor-pointer"
    >
      <div className="flex items-start gap-3 sm:gap-4 mb-4">
        <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 bg-muted rounded-lg overflow-hidden">
          <Image 
            src={job?.companyLogo} 
            alt={job?.companyLogoAlt}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-base sm:text-lg font-semibold text-foreground line-clamp-1">
              {job?.title}
            </h3>
            <button
              onClick={handleWishlistClick}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-muted transition-smooth"
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              <Icon 
                name={isWishlisted ? "Heart" : "Heart"} 
                size={20} 
                color={isWishlisted ? "var(--color-error)" : "var(--color-muted-foreground)"}
                className={isWishlisted ? "fill-current" : ""}
              />
            </button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-2">{job?.companyName}</p>
          
          <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Icon name="MapPin" size={14} />
              <span>{job?.location}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Briefcase" size={14} />
              <span>{job?.experience}</span>
            </div>
            {job?.isRemote && (
              <div className="flex items-center gap-1.5">
                <Icon name="Home" size={14} />
                <span>Remote</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md">
          {job?.jobType}
        </span>
        {job?.tags?.slice(0, 3)?.map((tag, index) => (
          <span 
            key={index}
            className="inline-flex items-center px-2.5 py-1 bg-muted text-muted-foreground text-xs rounded-md"
          >
            {tag}
          </span>
        ))}
        {job?.tags?.length > 3 && (
          <span className="text-xs text-muted-foreground">+{job?.tags?.length - 3} more</span>
        )}
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div>
          <p className="text-lg sm:text-xl font-semibold text-foreground">
            {formatSalary(job?.salaryMin, job?.salaryMax)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">per annum</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-xs text-muted-foreground hidden sm:block">
            {getTimeAgo(job?.postedDate)}
          </span>
          <Button 
            variant="default" 
            size="sm"
            onClick={handleApplyClick}
          >
            Apply Now
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 sm:hidden">
        <span className="text-xs text-muted-foreground">
          {getTimeAgo(job?.postedDate)}
        </span>
        {job?.applicants && (
          <span className="text-xs text-muted-foreground">
            {job?.applicants} applicants
          </span>
        )}
      </div>
    </div>
  );
};

export default JobCard;